import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff } from "lucide-react";
import PasswordStrength from "../components/PasswordStrength";
import LanguageSelector from "../components/LanguageSelector";

import { API } from "../utils/api";

// WEDO Logo Component
const WedoLogo = ({ variant = "color", className = "" }) => {
  const logoSrc = variant === "white" 
    ? "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/bdxr35h3_2024_10_make_it_matter.png"
    : "https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/fb48vbve_2024_10_make_it_matter_bleu.png";
  
  return (
    <img 
      src={logoSrc} 
      alt="WEDO - Make it matter" 
      className={className}
    />
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ email: "", password: "", name: "", language: i18n.language });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Redirect if already logged in
  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginData),
      });

      if (response.ok) {
        const data = await response.json();
        login(data.token, data.user);
        // Set language from user preference if available
        if (data.user?.language) {
          i18n.changeLanguage(data.user.language);
        }
        toast.success(t("auth.loginSuccess"));
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || t("auth.loginError"));
      }
    } catch (error) {
      toast.error(t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!acceptedTerms) {
      toast.error(t("auth.termsRequired"));
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...registerData, language: i18n.language }),
      });

      if (response.ok) {
        const data = await response.json();
        login(data.token, data.user);
        toast.success(t("auth.registerSuccess"));
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || t("auth.registerError"));
      }
    } catch (error) {
      toast.error(t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Gradient Background using provided image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_c49934ec-d4bd-49c0-9c0a-fa5e40ba77a6/artifacts/l79ii873_degrad%C3%A9_wedo_make_it_matter.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-white/90 hover:text-white transition-colors w-fit"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t("common.back")}
          </button>
          
          <div className="flex flex-col items-center justify-center flex-1">
            <WedoLogo variant="white" className="h-32 w-auto mb-6" />
            <p className="text-2xl text-white font-semibold mb-2">FLOW</p>
            <p className="text-white/90 text-center max-w-md text-lg mb-4">
              90 jours pour retrouver ton rythme
            </p>
            <p className="text-white/70 text-center max-w-sm text-sm">
              Clarté. Énergie. Posture. Dans la durée.
            </p>
          </div>
          
          <p className="text-white/70 text-sm text-center">
            coaching & conseil
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <WedoLogo className="h-16 w-auto" />
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-[#101E4D] text-center">
                {t("auth.login")}
              </CardTitle>
              <CardDescription className="text-center text-slate-500">
                {t("landing.heroSubtitle").substring(0, 60)}...
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Tabs */}
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 rounded-xl p-1">
                  <TabsTrigger 
                    value="login" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#101E4D] data-[state=active]:shadow-sm"
                    data-testid="login-tab"
                  >
                    {t("auth.login")}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register"
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#101E4D] data-[state=active]:shadow-sm"
                    data-testid="register-tab"
                  >
                    {t("auth.register")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-700">{t("auth.email")}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="vous@exemple.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          className="pl-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D] rounded-xl"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-slate-700">{t("auth.password")}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 pr-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D] rounded-xl"
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-[#101E4D] hover:bg-[#0A1330] text-white rounded-xl font-medium"
                      data-testid="login-submit-btn"
                    >
                      {loading ? t("common.loading") : t("auth.loginButton")}
                    </Button>
                    <div className="text-center pt-2">
                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="text-sm text-slate-500 hover:text-[#101E4D] transition-colors"
                        data-testid="forgot-password-link"
                      >
                        {t("auth.forgotPassword")}
                      </button>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-700">{t("auth.name")}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Jean Dupont"
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          className="pl-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D] rounded-xl"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-slate-700">{t("auth.email")}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="vous@exemple.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className="pl-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D] rounded-xl"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-slate-700">{t("auth.password")}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-password"
                          type={showRegisterPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className="pl-10 pr-10 h-12 bg-slate-50 border-slate-200 focus:border-[#101E4D] focus:ring-[#101E4D] rounded-xl"
                          required
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          tabIndex={-1}
                        >
                          {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <PasswordStrength password={registerData.password} />
                    </div>
                    
                    {/* Language selector */}
                    <div className="space-y-2">
                      <Label className="text-slate-700">{t("auth.selectLanguage")}</Label>
                      <LanguageSelector showLabel={false} />
                    </div>
                    
                    {/* Terms and Privacy consent */}
                    <div className="flex items-start space-x-3 pt-2">
                      <Checkbox
                        id="accept-terms"
                        checked={acceptedTerms}
                        onCheckedChange={setAcceptedTerms}
                        className="mt-1"
                        data-testid="accept-terms-checkbox"
                      />
                      <label
                        htmlFor="accept-terms"
                        className="text-sm text-slate-600 leading-relaxed cursor-pointer"
                      >
                        {t("auth.acceptTerms")}{" "}
                        <button
                          type="button"
                          onClick={() => window.open("/terms", "_blank")}
                          className="text-[#FF6B35] hover:underline"
                        >
                          {t("auth.termsOfService")}
                        </button>
                        {" "}{t("common.and")}{" "}
                        <button
                          type="button"
                          onClick={() => window.open("/privacy", "_blank")}
                          className="text-[#FF6B35] hover:underline"
                        >
                          {t("auth.privacyPolicy")}
                        </button>
                      </label>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-[#101E4D] hover:bg-[#0A1330] text-white rounded-xl font-medium"
                      data-testid="register-submit-btn"
                    >
                      {loading ? t("common.loading") : t("auth.registerButton")}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Mobile back button */}
          <button
            onClick={() => navigate("/")}
            className="lg:hidden flex items-center justify-center w-full mt-6 text-slate-500 hover:text-[#101E4D] transition-colors"
            data-testid="mobile-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.back")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
